import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/common";
import { useResource } from "@/hooks/use-resource";
import { CaseRow } from "@/components/work/CaseRow";
import { BarAction, PageBar } from "@/components/layout/PageBar";
import { SEGMENTS, SORTS, groupCases, matchesQuery, isFixture } from "@/components/work/workModel";

const SORT_KEY = "los.work.sort";

const PRIMARY = ["alle", "deg", "aktive", "tilbud"];

function Segments({ rows, value, onChange }) {
  const counts = useMemo(() => Object.fromEntries(SEGMENTS.map((s) => [s.key, rows.filter(s.match).length])), [rows]);
  const shown = SEGMENTS.filter((s) => PRIMARY.includes(s.key) || s.key === value || (s.key === "utkast" && counts.utkast));
  return (
    <div className="flex max-w-full flex-wrap items-center gap-1" role="tablist" aria-label="Utvalg" data-testid="work-segments">
      {shown.filter((s) => s.key === "alle" || counts[s.key] || s.key === value).map((s) => (
        <button key={s.key} type="button" role="tab" aria-selected={value === s.key} onClick={() => onChange(s.key)} className={cn("relative flex h-9 items-center gap-1.5 px-2.5 text-[14.5px] transition-colors hover:text-foreground", value === s.key ? "font-medium text-foreground after:absolute after:inset-x-2.5 after:bottom-0 after:h-[2px] after:rounded-full after:bg-foreground" : "text-foreground/55")} data-testid={`work-segment-${s.key}`}>
          {s.label} {s.key !== "alle" ? <span className="font-mono text-[12px] tabular-nums opacity-60">{counts[s.key]}</span> : null}
        </button>
      ))}
    </div>
  );
}

function GroupHeader({ g, open, onToggle }) {
  return (
    <div className="flex items-baseline gap-2.5 pb-1 pt-2" data-testid={`work-group-${g.key}`}>
      {g.collapsed ? (
        <button type="button" onClick={onToggle} aria-expanded={open} className="inline-flex items-center gap-1.5 rounded-sm text-lg font-semibold tracking-[-0.01em] text-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40" data-testid={`work-group-toggle-${g.key}`}>
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", !open && "-rotate-90")} />{g.label}
        </button>
      ) : <span className="text-lg font-semibold tracking-[-0.01em] text-foreground">{g.label}</span>}
      <span className="font-mono text-[13px] tabular-nums text-muted-foreground">{g.list.length}</span>
    </div>
  );
}

const EMPTY = {
  alle: ["Ingen saker ennå", "Slipp en forespørsel i samtalen på startsiden – saken åpnes som utkast og bekreftes når kunde og fartøy stemmer."],
  utkast: ["Ingen utkast", "Alt som er lastet opp er bekreftet eller forkastet."],
  deg: ["Ingenting venter på deg", "Ingen åpne avklaringer, linjer å vurdere, nære frister eller nevninger."],
  nevnt: ["Ingen nevner deg", "Når noen skriver @deg i et internt notat, dukker saken opp her til du har åpnet den."],
  aktive: ["Ingen aktive saker", "Bekreft et utkast for å få en aktiv sak."],
  tilbud: ["Ingen tilbud ennå", "Opprett en tilbudsrevisjon fra Tilbud-fanen når kalkylen er komplett."],
  avsluttet: ["Ingen avsluttede saker", ""],
  forkastet: ["Ingen forkastede utkast", "Forkastede utkast kan gjenopprettes her."],
};

/** /arbeid – the work surface: drafts, what needs you, active cases and quotes, with the next step on every row. */
export default function ProjectsPage() {
  const navigate = useNavigate();
  const { data, loading } = useResource("/projects", { limit: 100 });
  const [seg, setSeg] = useState("alle");
  const [q, setQ] = useState("");
  const [sort, setSortState] = useState(() => localStorage.getItem(SORT_KEY) || "aktiv");
  const [openClosed, setOpenClosed] = useState(false);
  const setSort = (v) => { setSortState(v); localStorage.setItem(SORT_KEY, v); };
  const rows = useMemo(() => (data || []).filter((p) => p.status !== "new" && !isFixture(p)), [data]);
  const list = useMemo(() => {
    const s = SEGMENTS.find((x) => x.key === seg) || SEGMENTS[0];
    const cmp = (SORTS.find((x) => x[0] === sort) || SORTS[0])[2];
    return rows.filter((p) => s.match(p) && matchesQuery(p, q.trim())).sort(cmp);
  }, [rows, seg, q, sort]);
  const groups = useMemo(() => (seg === "alle" && !q.trim() ? groupCases(list) : [{ key: seg, label: null, list }]), [list, seg, q]);
  const [emptyTitle, emptyText] = EMPTY[seg] || EMPTY.alle;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 pb-16 pt-5 sm:px-8" data-testid="projects-page">
      <PageBar title="Arbeid" testId="projects-bar" action={<BarAction onClick={() => navigate("/ny")} testId="projects-new-button"><Plus className="h-4 w-4" strokeWidth={2.2} /> Nytt arbeid</BarAction>} />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-black/[0.07]">
        <Segments rows={rows} value={seg} onChange={setSeg} />
        <div className="flex w-full items-center gap-2 pb-1.5 sm:w-auto">
          <label className="flex h-9 min-w-0 flex-1 items-center gap-2 text-[14px] sm:w-[260px] sm:flex-none">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i arbeid …" className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground/60" aria-label="Søk i saker" data-testid="projects-search-input" />
          </label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 shrink-0 cursor-pointer rounded-[8px] bg-transparent px-1.5 text-[13.5px] text-foreground/70 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/15" aria-label="Sortering" data-testid="work-sort">
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4" data-testid="projects-list">
        {loading ? <div className="space-y-2 pt-2">{[0, 1, 2, 3, 4].map((k) => <Skeleton key={k} className="h-20 rounded-[14px]" />)}</div> : !list.length ? (
          <div className="px-6 py-16 text-center" data-testid="work-empty">
            <div className="text-[15px] font-medium">{emptyTitle}</div>
            {emptyText ? <p className="mx-auto mt-1 max-w-[460px] text-[14px] text-muted-foreground">{emptyText}</p> : null}
          </div>
        ) : groups.map((g, gi) => {
          const open = !g.collapsed || openClosed;
          return (
            <section key={g.key} className={cn(gi > 0 && "mt-10")} data-testid={`work-section-${g.key}`}>
              {g.label ? <GroupHeader g={g} open={open} onToggle={() => setOpenClosed((v) => !v)} /> : null}
              {open ? <ul className="space-y-0.5">{g.list.map((p) => <CaseRow key={p.id} p={p} />)}</ul> : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
